import React, { useState, useEffect } from 'react';
import { 
  Box, 
  Container, 
  Typography, 
  Paper,
  ToggleButton, 
  ToggleButtonGroup,
  useTheme,
  useMediaQuery,
  CircularProgress,
  Alert
} from '@mui/material';
import { DataGrid } from '@mui/x-data-grid';
import { collection, query, where, onSnapshot } from 'firebase/firestore';
import { db } from '../firebase/config';

// Helper function to format seconds into HH:MM:SS
const formatTime = (seconds) => {
  if (!seconds || seconds === '-') return '-';
  
  const totalSeconds = parseInt(seconds);
  const hours = Math.floor(totalSeconds / 3600);
  const minutes = Math.floor((totalSeconds % 3600) / 60);
  const secs = totalSeconds % 60;
  
  return `${hours}:${minutes.toString().padStart(2, '0')}:${secs.toString().padStart(2, '0')}`;
};

// Helper function to calculate pace in minutes per mile
const calculatePace = (timeInSeconds, distance) => {
  if (!timeInSeconds || timeInSeconds === '-') return '-';
  
  const totalSeconds = parseInt(timeInSeconds);
  
  // Convert distances to miles (from kilometers)
  const distanceInMiles = distance === 'full' ? 26.2 : 13.1; // Full marathon = 26.2 miles, Half = 13.1 miles
  
  // Calculate pace in seconds per mile
  const paceInSecondsPerMile = totalSeconds / distanceInMiles;
  
  // Convert to minutes and seconds
  const paceMinutes = Math.floor(paceInSecondsPerMile / 60);
  const paceSeconds = Math.floor(paceInSecondsPerMile % 60);
  
  return `${paceMinutes}:${paceSeconds.toString().padStart(2, '0')}/mi`;
};

// Desktop (full) columns - compact widths
const desktopColumns = [
  { field: 'rank', headerName: 'Rank', width: 80, headerAlign: 'center', align: 'center', cellClassName: 'col-rank', flex: 0 },
  { field: 'racerNumber', headerName: '#', width: 80, headerAlign: 'center', align: 'center', flex: 0 },
  // name allowed to grow more than other columns
  { field: 'name', headerName: 'Name', minWidth: 140, headerAlign: 'left', align: 'left', cellClassName: 'col-name', flex: 2 },
  { field: 'age', headerName: 'Age', width: 60, headerAlign: 'center', align: 'center', flex: 0 },
  { field: 'time', headerName: 'Time', width: 90, headerAlign: 'center', align: 'center', cellClassName: 'col-time', flex: 0 },
  { field: 'pace', headerName: 'Pace', width: 90, headerAlign: 'center', align: 'center', flex: 0 }
];

// Mobile (compact) columns - minimal widths and ellipsize name
const mobileColumns = [
  { field: 'rank', headerName: 'Rank', width: 70, headerAlign: 'center', align: 'center', cellClassName: 'col-rank' },
  { field: 'racerNumber', headerName: '#', width: 70, headerAlign: 'center', align: 'center' },
  { field: 'firstName', headerName: 'Name', minWidth: 100, maxWidth: 180, headerAlign: 'left', align: 'left', cellClassName: 'col-name' },
  { field: 'time', headerName: 'Time', width: 90, headerAlign: 'center', align: 'center', cellClassName: 'col-time' }
];

const ResultsPage = () => {
  const [selectedDistance, setSelectedDistance] = useState('full');
  const [registrations, setRegistrations] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));

  // Real-time Firestore listener with automatic caching
  useEffect(() => {
    setLoading(true);
    setError('');
    
    // Query for registered participants only
    const registrationsRef = collection(db, 'registrations');
    const q = query(registrationsRef, where('status', '==', 'registered'));
    
    // Set up real-time listener
    const unsubscribe = onSnapshot(
      q,
      {
        // Enable cache persistence
        includeMetadataChanges: true
      },
      (snapshot) => {
        try {
          const transformedData = [];
          
          snapshot.forEach((doc) => {
            const data = doc.data();
            transformedData.push({
              id: doc.id,
              racerNumber: data.racer_id,
              name: `${data.firstName} ${data.lastName}`,
              firstName: data.firstName,
              age: data.age || '-',
              gender: data.gender || '-',
              time: formatTime(data.time),
              timeInSeconds: data.time || null, // Keep raw time for sorting
              pace: calculatePace(data.time, data.distance),
              distance: data.distance
            });
          });
          
          // Calculate ranks by sorting by time (fastest first) within each distance
          const fullMarathonData = transformedData.filter(r => r.distance === 'full');
          const halfMarathonData = transformedData.filter(r => r.distance === 'half');
          
          // Sort each distance group by time (fastest first)
          const sortByTime = (a, b) => {
            // Handle cases where time is null/undefined (no finish time yet)
            if (!a.timeInSeconds && !b.timeInSeconds) return 0;
            if (!a.timeInSeconds) return 1;  // Put runners without times at end
            if (!b.timeInSeconds) return -1;
            return a.timeInSeconds - b.timeInSeconds; // Ascending (fastest first)
          };
          
          fullMarathonData.sort(sortByTime);
          halfMarathonData.sort(sortByTime);
          
          // Assign ranks within each distance, handling ties
          const assignRanks = (data) => {
            for (let i = 0; i < data.length; i++) {
              const runner = data[i];
              
              if (!runner.timeInSeconds) {
                runner.rank = null; // No rank for runners without finish time
                continue;
              }
              
              if (i === 0) {
                // First runner gets rank 1
                runner.rank = 1;
              } else {
                const previousRunner = data[i - 1];
                if (runner.timeInSeconds === previousRunner.timeInSeconds) {
                  // Same time as previous runner = same rank
                  runner.rank = previousRunner.rank;
                } else {
                  // Different time = new rank (skip ranks if there were ties)
                  runner.rank = i + 1;
                }
              }
            }
          };
          
          assignRanks(fullMarathonData);
          assignRanks(halfMarathonData);
          
          // Combine back together
          const rankedData = [...fullMarathonData, ...halfMarathonData];
          
          setRegistrations(rankedData);
          setLoading(false);
          
          // Log if data came from cache
          const source = snapshot.metadata.fromCache ? 'local cache' : 'server';
          console.log(`📊 Loaded ${rankedData.length} registrations from ${source}`);
        } catch (err) {
          console.error('Error processing registrations:', err);
          setError('Error loading registrations');
          setLoading(false);
        }
      },
      (err) => {
        console.error('Firestore listener error:', err);
        setError('Unable to connect to database. Please check your internet connection.');
        setLoading(false);
      }
    );
    
    // Cleanup listener on unmount
    return () => unsubscribe();
  }, []);

  // DataGrid props that differ between mobile and desktop
  const gridDesktopProps = {
    disableColumnSelector: false,
    disableColumnFilter: false,
    disableColumnMenu: false,
  };
  const gridMobileProps = {
    disableColumnSelector: true,
    disableColumnFilter: true,
    disableColumnMenu: true,
  };

  const handleDistanceChange = (event, newDistance) => {
    if (newDistance !== null) {
      setSelectedDistance(newDistance);
    }
  };

  // Filter registrations by selected distance and sort by rank (fastest first)
  const filteredData = registrations
    .filter(reg => reg.distance === selectedDistance)
    .sort((a, b) => {
      // Sort by rank (1st, 2nd, 3rd, etc.)
      if (!a.rank && !b.rank) return 0;
      if (!a.rank) return 1;  // Put unranked at end
      if (!b.rank) return -1;
      return a.rank - b.rank; // Ascending rank (1, 2, 3...)
    });

  return (
    <Box sx={{ py: 4 }}>
      <Container maxWidth="lg">
        <Box sx={{ mb: 6, textAlign: 'center' }}>
          <Typography 
            variant="h2" 
            component="h1" 
            sx={{ 
              fontWeight: 900,
              mb: 2,
              background: 'linear-gradient(90deg, #3FAF5D 0%, #1C4C89 100%)',
              WebkitBackgroundClip: 'text',
              color: 'transparent'
            }}
          >
            RACE RESULTS
          </Typography>
          <Typography variant="h6" sx={{ color: 'text.secondary', mb: 4 }}>
            View the results from our latest event
          </Typography>

          <ToggleButtonGroup
            value={selectedDistance}
            exclusive
            onChange={handleDistanceChange}
            aria-label="race distance"
            orientation={isMobile ? 'vertical' : 'horizontal'}
            sx={{ mb: 4, width: isMobile ? '100%' : 'auto', '& .MuiToggleButton-root': { width: isMobile ? '100%' : 'auto' } }}
          >
            <ToggleButton 
              value="full" 
              aria-label="full marathon"
              sx={{
                px: 4,
                py: 1.5,
                '&.Mui-selected': {
                  background: 'linear-gradient(90deg, #3FAF5D 0%, #1C4C89 100%)',
                  color: 'white',
                  '&:hover': {
                    background: 'linear-gradient(90deg, #3FAF5D 0%, #1C4C89 100%)',
                  }
                }
              }}
            >
              Full Marathon
            </ToggleButton>
            <ToggleButton 
              value="half" 
              aria-label="half marathon"
              sx={{
                px: 4,
                py: 1.5,
                '&.Mui-selected': {
                  background: 'linear-gradient(90deg, #3FAF5D 0%, #1C4C89 100%)',
                  color: 'white',
                  '&:hover': {
                    background: 'linear-gradient(90deg, #3FAF5D 0%, #1C4C89 100%)',
                  }
                }
              }}
            >
              Half Marathon
            </ToggleButton>
          </ToggleButtonGroup>
        </Box>

        {error && (
          <Alert severity="error" sx={{ mb: 3 }}>
            {error}
          </Alert>
        )}

        {loading ? (
          <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: 400 }}>
            <CircularProgress size={60} />
          </Box>
        ) : filteredData.length === 0 ? (
          <Alert severity="info">
            No registered participants for {selectedDistance === 'full' ? 'Full' : 'Half'} Marathon yet.
          </Alert>
        ) : (
          <Paper 
          elevation={0}
          sx={{ 
            height: isMobile ? 'auto' : 635,
            width: '100%',
            borderRadius: 3,
            overflow: 'hidden',
            border: '1px solid rgba(0,0,0,0.1)',
            '& .MuiDataGrid-root': {
              border: 'none',
            },
            '& .MuiDataGrid-cell': {
              borderBottom: '1px solid rgba(0,0,0,0.05)',
            },
            '& .MuiDataGrid-columnHeaders': {
              backgroundColor: 'rgba(0,0,0,0.02)',
              borderBottom: '1px solid rgba(0,0,0,0.1)',
            },
            '& .MuiDataGrid-footerContainer': {
              borderTop: '1px solid rgba(0,0,0,0.1)',
            }
          }}
        >
          <DataGrid
            rows={filteredData}
            columns={isMobile ? mobileColumns : desktopColumns}
            pageSize={isMobile ? 5 : 10}
            rowsPerPageOptions={isMobile ? [5] : [10]}
            autoHeight={isMobile}
            disableSelectionOnClick
            density="compact"
            {...(isMobile ? gridMobileProps : gridDesktopProps)}
            sx={{
              '& .MuiDataGrid-row:hover': {
                backgroundColor: 'rgba(0,0,0,0.02)',
              },
              '& .MuiDataGrid-cell': {
                py: 0.4,
                px: 0.75,
              },
              '& .MuiDataGrid-cell.col-rank': {
                px: 2.5,
                fontWeight: 700,
              },
              '& .MuiDataGrid-cell.col-name': {
                px: 0.5,
                py: 0.3,
                whiteSpace: 'nowrap',
                overflow: 'hidden',
                textOverflow: 'ellipsis',
                maxWidth: '100%'
              },
              '& .MuiDataGrid-cell.col-time': {
                px: 0.5,
                py: 0.3,
                textAlign: 'center'
              },
              // Truncate header text if it overflows
              '& .MuiDataGrid-columnHeaderTitle': {
                whiteSpace: 'nowrap',
                overflow: 'hidden',
                textOverflow: 'ellipsis'
              },
              '& .MuiDataGrid-columnHeader': {
                py: 0.5,
              }
            }}
          />
        </Paper>
        )}
      </Container>
    </Box>
  );
};

export default ResultsPage;